import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCalendar,
  faMapMarkerAlt,
  faStickyNote,
  faFile,
  faBriefcase,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { hasPermission } from "../../../utils/permissionUtils";
import {
  approveBusinessTrip,
  rejectBusinessTrip,
} from "../../../api/hrm/business_trips/businessTrip";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { formatDateVN } from "../../../utils/formatUtils";

const PendingBusinessTripList = ({
  data,
  currentUserId,
  onApproved,
  onReject,
  onDelete,
  onUpdated,
}) => {
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  if (!data || data.length === 0) return null;

  return (
    <div className="mb-6">
      <h2 className="font-semibold text-base text-gray-700 mb-3 ml-2">
        Phiếu công tác chờ duyệt ({data.length} phiếu)
      </h2>

      {/* Danh sách Card */}
      <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {data.map((trip) => {
          const start = new Date(trip.start_date);
          const end = new Date(trip.end_date);
          const dayCount =
            Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1) || 1;

          return (
            <div
              key={trip.id}
              onClick={() => setSelectedTrip(trip)}
              className="relative bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md cursor-pointer transition-all p-4 overflow-hidden group"
            >
              {/* 🏷️ Tag góc trên bên phải */}
              <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                <span className="text-[11px] px-2 py-[2px] rounded-full bg-blue-100 text-blue-700 font-medium border border-blue-200 shadow-sm">
                  {dayCount} ngày
                </span>
                <span className="text-[11px] px-2 py-[2px] rounded-full bg-green-100 text-green-700 font-medium border border-green-200 shadow-sm">
                  {trip.budget_cost
                    ? `${Number(trip.budget_cost).toLocaleString("vi-VN")} ₫`
                    : "0 ₫"}
                </span>
              </div>

              {/* Nội dung chính */}
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center text-base font-semibold text-red-700">
                  <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-400" />
                  {trip.employee_name?.toUpperCase()}
                </div>
              </div>

              <div className="flex items-center text-sm text-gray-600 mb-1">
                <FontAwesomeIcon icon={faCalendar} className="mr-2 text-gray-400" />
                {formatDateVN(trip.start_date)} → {formatDateVN(trip.end_date)}
              </div>

              <div className="flex items-center text-sm text-gray-600 mb-1">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-gray-400" />
                {trip.location}
              </div>

              <div className="flex items-start text-sm text-gray-600 mb-1">
                <FontAwesomeIcon
                  icon={faStickyNote}
                  className="mr-2 text-gray-400 mt-0.5"
                />
                <span className="line-clamp-2">{trip.reason}</span>
              </div>

              {/* 🔘 Nút Xóa góc dưới bên phải (chỉ người tạo mới thấy) */}
              {trip.employee_id === currentUserId && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const result = await confirmDelete({
                      type: DELETE_ENTITY.business_trip,
                      name: trip?.trip_code || trip?.destination,
                      context: { employeeName: trip?.employee_name },
                    });
                    if (result.isConfirmed && typeof onDelete === "function") {
                      onDelete(trip);
                    }
                  }}
                  title="Xóa phiếu công tác"
                  className="absolute bottom-2 right-2 text-gray-300 hover:text-red-700 transition-all opacity-80 hover:opacity-100"
                >
                  <FontAwesomeIcon icon={faTrashAlt} className="text-lg" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Popup chi tiết */}
      {selectedTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg relative overflow-hidden border border-gray-200 animate-popupShow">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faBriefcase} className="text-red-600" />
                Chi tiết đăng ký công tác
              </h2>
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-300">
                Chờ duyệt
              </span>
            </div>

            {/* Nội dung */}
            <div className="p-6 space-y-4 text-sm text-gray-800">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-700 mb-2">
                  Thông tin nhân viên
                </h3>
                <p>
                  <strong>Nhân viên:</strong> {selectedTrip.employee_name}
                </p>
                <p>
                  <strong>Bộ phận:</strong> {selectedTrip.department_name || "-"}
                </p>
                <p>
                  <strong>Chi nhánh:</strong> {selectedTrip.branch_name || "-"}
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="font-semibold text-gray-700 mb-2">
                  Thông tin công tác
                </h3>
                <p>
                  <strong>Thời gian:</strong>{" "}
                  <span
                    style={{
                      backgroundColor: "#e0f2ff",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      display: "inline-block",
                      marginRight: "6px",
                    }}
                  >
                    Từ ({formatDateVN(selectedTrip.start_date)})
                  </span>

                  <span
                    style={{
                      backgroundColor: "#e0ffe7",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      display: "inline-block",
                    }}
                  >
                    đến ({formatDateVN(selectedTrip.end_date)})
                  </span>
                </p>

                <p>
                  <strong>Địa điểm:</strong> {selectedTrip.location}
                </p>
                <p>
                  <strong>Lý do:</strong> {selectedTrip.reason}
                </p>
                <p>
                  <strong>Ngân sách dự kiến:</strong>{" "}
                  {selectedTrip.budget_cost
                    ? `${Number(selectedTrip.budget_cost).toLocaleString("vi-VN")} VNĐ`
                    : "0 VNĐ"}
                </p>

                {selectedTrip.attached_file && (
                  <p className="flex items-center gap-2 mt-2">
                    <FontAwesomeIcon icon={faFile} className="text-gray-500" />
                    <a
                      href={selectedTrip.attached_file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Xem file đính kèm
                    </a>
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
              {/* Nhóm nút trái */}
              <div className="flex gap-3">
                {hasPermission("hrm.business_trip", "A") && (
                  <>
                    {/* ✅ Duyệt */}
                    <button
                      onClick={async () => {
                        try {
                          await approveBusinessTrip(selectedTrip.id, currentUserId);
                          showSuccessToast("Đã phê duyệt công tác thành công!");
                          setSelectedTrip(null);
                          onApproved?.();
                          onUpdated?.();
                        } catch (error) {
                          console.error("❌ Lỗi phê duyệt công tác:", error);
                          showErrorToast("Không thể phê duyệt công tác.");
                        }
                      }}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow-sm transition"
                    >
                      Duyệt
                    </button>

                    {/* ❌ Không duyệt */}
                    <button
                      onClick={() => {
                        setRejectReason("");
                        setShowRejectPopup(true);
                      }}
                      className="px-4 py-2 bg-white border border-red-400 text-red-600 rounded-lg font-medium hover:bg-red-50 transition"
                    >
                      Không duyệt
                    </button>
                  </>
                )}
              </div>

              {/* Nút phải */}
              <button
                onClick={() => setSelectedTrip(null)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium shadow-sm hover:bg-gray-100 transition"
              >
                Đóng
              </button>
            </div>
          </div>

          {/* Popup nhập lý do không duyệt */}
          {showRejectPopup && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
              <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-sm border">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Nhập lý do không duyệt
                </h3>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  placeholder="Nhập lý do..."
                  className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-400"
                />
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={() => setShowRejectPopup(false)}
                    className="px-4 py-2 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-100"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={async () => {
                      if (!rejectReason.trim()) {
                        showErrorToast("Vui lòng nhập lý do không duyệt.");
                        return;
                      }
                      try {
                        await rejectBusinessTrip(
                          selectedTrip.id,
                          currentUserId,
                          rejectReason
                        );
                        showSuccessToast("Đã từ chối công tác!");
                        setShowRejectPopup(false);
                        setSelectedTrip(null);
                        onReject?.();
                        onUpdated?.();
                      } catch (error) {
                        console.error("❌ Lỗi khi từ chối công tác:", error);
                        showErrorToast("Không thể từ chối công tác.");
                      }
                    }}
                    className="px-4 py-2 rounded-lg text-sm border border-red-400 text-red-600 bg-red-50 hover:bg-red-100"
                  >
                    Xác nhận
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* CSS animation */}
          <style>{`
            @keyframes popupShow {
              0% { transform: scale(0.95); opacity: 0; }
              100% { transform: scale(1); opacity: 1; }
            }
            .animate-popupShow {
              animation: popupShow 0.25s ease-out forwards;
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default PendingBusinessTripList;
